import { Alert, BoxDropFile, TextEditor } from '@iveco-ti/iveco-ds'
import { ImageUp, ImageUpscale, Lightbulb } from 'lucide-react'
import { useTranslation } from 'react-i18next'

type FormNewsContentProps = {
  aspectRatio: string
  sizes: {
    ideal: string
    firstOption: string
    secondOption: string
  }
}

export function FormNewsContent({ aspectRatio, sizes }: FormNewsContentProps) {
  const { t } = useTranslation('panel')

  return (
    <div className="flex flex-col gap-6 pb-3">
      <h2 className="text-2xl text-sky-700 font-semibold">
        {t('news.drawer_create_news.content.title')}
      </h2>

      <TextEditor.Root
        name="content"
        placeholder={t('news.drawer_create_news.fields.content.placeholder')}
      >
        <TextEditor.Toolbar>
          <TextEditor.ToolHeading />
          <TextEditor.ToolBold />
          <TextEditor.ToolItalic />
          <TextEditor.ToolUnderline />
          <TextEditor.ToolStrike />
          <TextEditor.ToolLink />
          <TextEditor.ToolOrderedList />
          <TextEditor.ToolBulletList />
          <TextEditor.ToolBlockquote />
          <TextEditor.ToolTextLeft />
          <TextEditor.ToolTextCenter />
          <TextEditor.ToolTextRight />
        </TextEditor.Toolbar>
      </TextEditor.Root>

      <BoxDropFile.Root>
        <BoxDropFile.Input
          name="image"
          title={t('news.drawer_create_news.fields.image.title')}
          description={t('news.drawer_create_news.fields.image.description')}
          accept={{ 'image/jpeg': [], 'image/png': [], 'image/webp': [] }}
        >
          <BoxDropFile.Icon icon={<ImageUp />} />
        </BoxDropFile.Input>
      </BoxDropFile.Root>

      <Alert.Root>
        <Alert.Icon icon={<ImageUpscale />} className="text-sky-700" />

        <Alert.Body>
          <Alert.Title
            text={t('news.drawer_create_news.content.alert.instructions.title')}
          />

          <Alert.Description>
            <p className="mb-1">
              {t(
                'news.drawer_create_news.content.alert.instructions.description',
              )}
            </p>

            <ul className="list-disc space-y-1">
              <li>
                {t(
                  'news.drawer_create_news.content.alert.instructions.list.aspect_ratio',
                  { aspectRatio },
                )}
              </li>
              <li>
                {t(
                  'news.drawer_create_news.content.alert.instructions.list.ideal_size',
                  { size: sizes.ideal },
                )}
              </li>
              <li>
                {t(
                  'news.drawer_create_news.content.alert.instructions.list.other_sizes',
                  {
                    first_option: sizes.firstOption,
                    second_option: sizes.secondOption,
                  },
                )}
              </li>
            </ul>
          </Alert.Description>
        </Alert.Body>
      </Alert.Root>

      <Alert.Root color="warning">
        <Alert.Icon icon={<Lightbulb />} />

        <Alert.Body>
          <Alert.Title
            text={t('news.drawer_create_news.content.alert.tip.title')}
          />

          <Alert.Description>
            {t('news.drawer_create_news.content.alert.tip.description')}
          </Alert.Description>
        </Alert.Body>
      </Alert.Root>
    </div>
  )
}
